'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

export type TenantId = 'main' | 'nanobanana';

interface TenantConfig {
  id: TenantId;
  name: string;
  description: string;
}

const TENANT_CONFIGS: Record<TenantId, TenantConfig> = {
  main: {
    id: 'main',
    name: 'MAIA Router',
    description: 'AI Model Router Platform',
  },
  nanobanana: {
    id: 'nanobanana',
    name: 'Nano Banana',
    description: 'AI Image Generation Model Showcase',
  },
};

interface TenantContextValue {
  tenant: TenantConfig;
  isNanoBanana: boolean;
  isMain: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Detect tenant from hostname
 * nanobanana.maiarouter.ai → 'nanobanana'
 * nanobanana.localhost → 'nanobanana' (for local dev)
 * everything else → 'main'
 */
function getTenantFromHostname(): TenantId {
  if (typeof window === 'undefined') return 'main';

  const hostname = window.location.hostname.toLowerCase();

  if (hostname.startsWith('nanobanana.')) {
    return 'nanobanana';
  }

  return 'main';
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const value = useMemo<TenantContextValue>(() => {
    const tenantId = getTenantFromHostname();
    return {
      tenant: TENANT_CONFIGS[tenantId],
      isNanoBanana: tenantId === 'nanobanana',
      isMain: tenantId === 'main',
    };
  }, []);

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Get tenant ID without React context (for use outside components)
 */
export function getCurrentTenant(): TenantId {
  return getTenantFromHostname();
}
